import { useRef, useCallback, useEffect } from 'react'
import { TIMELINE_DURATION_MINUTES, TIMELINE_START_HOUR, getDateIsoFromXPercent } from '../../../shared/lib/timeline'
import { ResizeHandle } from '../../../features/disposition'

type InteractionMode =
  | { type: 'resize'; handle: ResizeHandle; startX: number }
  | { type: 'move'; offsetMinutes: number; startX: number }

interface Options {
  anfahrtStartIso?: string
  onResizeStart?: (handle: ResizeHandle) => void
  onResizeMove?: (minutesDelta: number) => void
  onResizeEnd?: () => void
  onBlockMove?: (newAnfahrtStartIso: string) => void
  onBlockMoveEnd?: () => void
  onBlockMoveToRow?: (technicianId: string) => void
  dayIso: string
}

export function useTimelineTrackMouse({
  anfahrtStartIso,
  onResizeStart,
  onResizeMove,
  onResizeEnd,
  onBlockMove,
  onBlockMoveEnd,
  onBlockMoveToRow,
  dayIso,
}: Options) {
  const trackRef = useRef<HTMLDivElement>(null)
  const interactionRef = useRef<InteractionMode | null>(null)

  const getIsoFromMouseEvent = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      const xPercent = ((e.clientX - rect.left) / rect.width) * 100
      return getDateIsoFromXPercent(xPercent, dayIso)
    },
    [dayIso],
  )

  const handleResizeStart = useCallback(
    (handle: ResizeHandle, e: React.MouseEvent) => {
      e.preventDefault()
      interactionRef.current = { type: 'resize', handle, startX: e.clientX }
      onResizeStart?.(handle)
    },
    [onResizeStart],
  )

  const handleBlockDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (!anfahrtStartIso || !trackRef.current) return
      e.preventDefault()

      const rect = trackRef.current.getBoundingClientRect()
      const cursorPercent = ((e.clientX - rect.left) / rect.width) * 100
      const cursorMinutes = (cursorPercent / 100) * TIMELINE_DURATION_MINUTES

      const blockStartDate = new Date(anfahrtStartIso)
      const blockStartMinutes = blockStartDate.getHours() * 60 + blockStartDate.getMinutes() - TIMELINE_START_HOUR * 60
      const offsetMinutes = cursorMinutes - blockStartMinutes

      interactionRef.current = { type: 'move', offsetMinutes, startX: e.clientX }
    },
    [anfahrtStartIso],
  )

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const interaction = interactionRef.current
      if (!interaction || !trackRef.current) return

      const rect = trackRef.current.getBoundingClientRect()

      if (interaction.type === 'resize') {
        const deltaX = e.clientX - interaction.startX
        const deltaPercent = (deltaX / rect.width) * 100
        const deltaMinutes = Math.round(((deltaPercent / 100) * TIMELINE_DURATION_MINUTES) / 15) * 15
        onResizeMove?.(deltaMinutes)
      } else if (interaction.type === 'move') {
        // Detect cross-row movement
        const tracks = Array.from(document.querySelectorAll<HTMLElement>('[data-timeline-track]'))
        for (const track of tracks) {
          const trackRect = track.getBoundingClientRect()
          if (e.clientY >= trackRect.top && e.clientY <= trackRect.bottom) {
            const rowTechId = track.dataset.technicianId
            if (rowTechId) {
              onBlockMoveToRow?.(rowTechId)
            }
            break
          }
        }

        const cursorPercent = ((e.clientX - rect.left) / rect.width) * 100
        const blockStartPercent = cursorPercent - (interaction.offsetMinutes / TIMELINE_DURATION_MINUTES) * 100
        const newIso = getDateIsoFromXPercent(blockStartPercent, dayIso)
        onBlockMove?.(newIso)
      }
    }

    const handleMouseUp = () => {
      const interaction = interactionRef.current
      if (!interaction) return
      interactionRef.current = null

      if (interaction.type === 'resize') {
        onResizeEnd?.()
      } else if (interaction.type === 'move') {
        onBlockMoveEnd?.()
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [onResizeMove, onResizeEnd, onBlockMove, onBlockMoveEnd, onBlockMoveToRow, dayIso])

  const isInteracting = () => interactionRef.current !== null

  return {
    trackRef,
    getIsoFromMouseEvent,
    handleResizeStart,
    handleBlockDragStart,
    isInteracting,
  }
}
